import { useEffect } from "react";
import { AgentTask, AgentTaskStatus } from "@forgepoint/shared";
import { useOpportunitiesStore } from "@/modules/opportunities/opportunitiesStore";

/**
 * Finds the review task whose approval finalizes the given quote.
 * @param tasks - Tasks on the quote's opportunity
 * @param quoteId - Quote being finalized
 * @returns The FinalizeQuote task awaiting review, or null when there is none
 */
export const findPendingFinalizeTask = (
  tasks: AgentTask[],
  quoteId: string,
): AgentTask | null =>
  tasks.find(
    (task) =>
      task.status === AgentTaskStatus.IN_PROGRESS &&
      task.actionParameters?.actionId === "FinalizeQuote" &&
      task.actionParameters.parameters.quoteId === quoteId,
  ) ?? null;

/**
 * Reads the FinalizeQuote review task the agent raised for a quote, if any.
 * @param quoteId - Quote being edited
 * @param opportunityId - Opportunity the quote belongs to
 * @returns The task awaiting review, or null
 */
export function usePendingFinalizeTask(
  quoteId: string | undefined,
  opportunityId: string | null | undefined,
): AgentTask | null {
  const fetchOpportunityTasks = useOpportunitiesStore(
    (state) => state.fetchOpportunityTasks,
  );
  const tasks = useOpportunitiesStore((state) =>
    opportunityId ? state.opportunityTasks.get(opportunityId) : undefined,
  );

  useEffect(() => {
    if (!opportunityId) {
      return;
    }
    fetchOpportunityTasks(opportunityId);
  }, [opportunityId, fetchOpportunityTasks]);

  if (!quoteId || !opportunityId) {
    return null;
  }
  return findPendingFinalizeTask(tasks ?? [], quoteId);
}
